import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from '../librarianSwal';
import { useActiveLibraryId, useSectionBase } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  addBookCategory,
  updateBookCategory,
  getBookCategoryById
} from '../../api/api';

import '../BookList.css';
import '../lib-page-header.css';

const getErrorMessage = (error) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    'Something went wrong'
  );
};

const BookCategoryForm = () => {
  const navigate = useNavigate();
  const sectionBase = useSectionBase();
  const { id } = useParams();

  const libraryId = useActiveLibraryId();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active'
  });

  const fetchCategory = async () => {
    try {
      if (!id) return;

      setLoading(true);
      const data = await getBookCategoryById(id);

      setFormData({
        name: data?.name || '',
        description: data?.description || '',
        status: data?.status || 'active'
      });
    } catch (error) {
      console.error('Fetch category error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchCategory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      Swal.fire('Validation Error', 'Category name is required', 'warning');
      return false;
    }

    if (!libraryId) {
      Swal.fire('Error', 'Library ID not found. Please login again.', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const payload = {
      library_id: libraryId,
      name: formData.name.trim(),
      description: formData.description.trim(),
      status: formData.status
    };

    try {
      setSaving(true);

      if (isEdit) {
        await updateBookCategory(id, payload);
        Swal.fire('Success', 'Category updated successfully', 'success');
      } else {
        await addBookCategory(payload);
        Swal.fire('Success', 'Category added successfully', 'success');
      }

      navigate(`${sectionBase}/book-categories`);
    } catch (error) {
      console.error('Save category error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="book-list-page">
      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-tags" />
          </div>
          <div>
            <h3 className="lib-page-header__title">{isEdit ? 'Edit Book Category' : 'Add Book Category'}</h3>
            <div className="lib-page-header__subtitle">
              {isEdit ? 'Update existing book category' : 'Create new book category'}
            </div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button
            type="button"
            className="book-reset-btn"
            onClick={() => navigate(`${sectionBase}/book-categories`)}
          >
            Back
          </button>
        </div>
      </div>

      <div className="book-form-card">
        {showLoader ? (
          <Loader />
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="book-form-grid">
              <div className="book-form-group">
                <label>
                  Category Name <span>*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className="book-form-control"
                  placeholder="Enter category name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              <div className="book-form-group">
                <label>Status</label>
                <SearchableSelect
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' }
                  ]}
                  clearable={false}
                />
              </div>
            </div>

            <div className="book-form-group full">
              <label>Description</label>
              <textarea
                name="description"
                className="book-form-control textarea"
                placeholder="Enter description"
                value={formData.description}
                onChange={handleChange}
              />
            </div>

            <div className="book-form-actions">
              <button
                type="button"
                className="book-cancel-btn"
                onClick={() => navigate(`${sectionBase}/book-categories`)}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="book-save-btn"
                disabled={saving}
              >
                {saving
                  ? 'Saving...'
                  : isEdit
                    ? 'Update Category'
                    : 'Save Category'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookCategoryForm;